/**
 * Print Window Utility
 * Opens a separate window for printing to ensure clean, isolated print output
 * Works on both desktop and mobile devices
 */

/**
 * Opens a new window with the specified content and triggers print
 * @param {string} content - HTML content to print
 * @param {string} title - Window title
 * @param {object} options - Additional options
 * @param {string} options.pageSize - Page size (A4, 80mm, etc.)
 * @param {boolean} options.landscape - Landscape orientation
 */
export const openPrintWindow = (content, title = 'Print', options = {}) => {
  // Open a new tab (no size specs = tab, not popup — works on mobile)
  const printWindow = window.open('', '_blank');
  
  if (!printWindow) {
    alert('Please allow pop-ups to print');
    return;
  }

  // Determine page size CSS
  let pageSizeCSS = '';
  if (options.pageSize === 'thermal' || options.pageSize === '80mm') {
    pageSizeCSS = `
      @page {
        size: 80mm auto;
        margin: 0;
      }
      
      @import url('https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;700;900&display=swap');
      
      html, body {
        width: 80mm;
        margin: 0;
        padding: 0;
        height: auto !important;
        overflow: visible !important;
      }
      
      * {
        font-family: 'Roboto Mono', 'Courier New', monospace !important;
        color: #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
        font-weight: 700 !important;
        text-shadow: none !important;
        filter: none !important;
        transform: none !important;
        -webkit-font-smoothing: antialiased !important;
        -moz-osx-font-smoothing: grayscale !important;
      }
      
      strong, b, .font-bold, h1, h2, h3, h4, h5, h6 {
        font-weight: 900 !important;
        color: #000 !important;
      }
      
      hr, .border, [style*="border"], table, td, th {
        border-color: #000 !important;
      }
      
      table {
        border-collapse: collapse !important;
      }
      
      /* THERMAL ROLL: Completely disable all page breaks for continuous printing.
         Only .receipt-page-break is allowed to force breaks between receipts. */
      @media print {
        * {
          text-rendering: optimizeLegibility !important;
          page-break-before: auto !important;
          page-break-after: auto !important;
          page-break-inside: auto !important;
          break-before: auto !important;
          break-after: auto !important;
          break-inside: auto !important;
        }
        .receipt-page-break {
          page-break-after: always !important;
          break-after: page !important;
        }
      }
      
      /* PROVISIONAL WATERMARK - outline text, rotated diagonally */
      .provisional-watermark {
        position: fixed !important;
        top: 50% !important;
        left: 50% !important;
        transform: translate(-50%, -50%) rotate(-55deg) !important;
        font-size: 28px !important;
        font-weight: 900 !important;
        letter-spacing: 4px !important;
        color: transparent !important;
        -webkit-text-stroke: 1.5px #999 !important;
        text-transform: uppercase !important;
        pointer-events: none !important;
        z-index: 9999 !important;
        white-space: nowrap !important;
        opacity: 0.7 !important;
      }
      @media print {
        .provisional-watermark {
          position: fixed !important;
          color: transparent !important;
          -webkit-text-stroke: 1.5px #999 !important;
          opacity: 0.7 !important;
        }
      }
    `;
  } else if (options.pageSize === 'A4') {
    pageSizeCSS = `
      @page {
        size: A4 ${options.landscape ? 'landscape' : 'portrait'};
        margin: 15mm;
      }
      body {
        margin: 0;
        padding: 0;
      }
      .provisional-watermark {
        position: fixed !important;
        top: 50% !important;
        left: 50% !important;
        transform: translate(-50%, -50%) rotate(-45deg) !important;
        font-size: 72px !important;
        font-weight: 900 !important;
        letter-spacing: 8px !important;
        color: transparent !important;
        -webkit-text-stroke: 2px #bbb !important;
        text-transform: uppercase !important;
        pointer-events: none !important;
        z-index: 9999 !important;
        white-space: nowrap !important;
        opacity: 0.6 !important;
      }
    `;
  } else {
    pageSizeCSS = `
      @page {
        margin: 0.5cm;
      }
      body {
        margin: 0;
        padding: 0;
      }
    `;
  }

  // Build the complete HTML document
  const html = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${title}</title>
      <style>
        /* Reset styles */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        /* Page size and orientation */
        ${pageSizeCSS}

        /* Print-specific styles */
        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .no-print {
            display: none !important;
          }
        }

        /* Base styles for content */
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          background: white;
        }

        /* Ensure images load properly */
        img {
          max-width: 100%;
          height: auto;
        }

        /* Table styles */
        table {
          border-collapse: collapse;
          width: 100%;
        }

        /* Loading message */
        #loading-message {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
          font-size: 18px;
          color: #666;
        }

        /* Hide loading message when content loads */
        body.loaded #loading-message {
          display: none;
        }
      </style>
    </head>
    <body>
      <div id="loading-message">Preparing print preview...</div>
      <div id="print-content" style="display: none;">
        ${content}
      </div>
      <script>
        // Wait for content and images to load
        window.addEventListener('load', function() {
          // Show content and hide loading
          document.body.classList.add('loaded');
          document.getElementById('print-content').style.display = 'block';
          
          // Small delay to ensure rendering is complete
          setTimeout(function() {
            // Trigger print dialog
            window.print();
          }, 500);
        });
      </script>
    </body>
    </html>
  `;

  // Write the HTML to the new window
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
};

/**
 * Get HTML content from a React component by ID
 * @param {string} elementId - ID of the element to get content from
 * @returns {string} HTML content
 */
export const getElementHTML = (elementId) => {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`Element with ID "${elementId}" not found`);
    return '<p>Error: Content not found</p>';
  }
  return element.innerHTML;
};

/**
 * Get outer HTML including the element itself
 * @param {string} elementId - ID of the element
 * @returns {string} Outer HTML content
 */
export const getElementOuterHTML = (elementId) => {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`Element with ID "${elementId}" not found`);
    return '<p>Error: Content not found</p>';
  }
  return element.outerHTML;
};
